import { Fragment, useContext } from 'react';

import { CurrencyContext } from 'app/components/commons/Currency/CurrencyContext/CurrencyContext';
import { computeExperienceName } from 'app/components/commons/Experience/utils';
import { useExperiencesCategories } from 'app/hooks/data/useExperience';
import type { ExperienceCategory } from 'app/typings/experiences';
import type {
  BookingDetails,
  ExtranetBookingExperience,
} from 'app/typings/extranetBookings/booking';

import { RenderPaymentDetail } from './RenderPaymentDetail';

type RenderExtraProps = {
  booking: BookingDetails;
};

export const RenderExtra = ({ booking }: RenderExtraProps) => {
  const { currencyFormatter } = useContext(CurrencyContext);
  const { data: experienceCategories } = useExperiencesCategories();

  const extras = booking.experiences.filter(
    (exp) => !exp.included && (exp.quantity ?? 0) > 0 && exp.hotelId
  );

  const staycationExperiences = booking.experiences.filter(
    (exp) => !exp.hotelId
  );

  if (!staycationExperiences.length && !extras.length) {
    return <></>;
  }

  const renderExperienceName = (
    experienceCategory: ExperienceCategory,
    exp: ExtranetBookingExperience
  ) => {
    return computeExperienceName({
      category: experienceCategory,
      quantity: null,
      brand: exp.brand ?? null,
      duration: exp.duration ?? null,
      durationDescription: exp.durationDescription ?? null,
      time: exp.time ?? '',
      locations: exp.locations ?? null,
    });
  };

  return (
    <div className="payment-detail">
      {staycationExperiences.map((stayExp, idx) => {
        const expQuantity = stayExp.included ? 1 : stayExp.quantity;

        const title = idx === 0 ? 'Extras' : undefined;
        const removeDivider =
          idx !== staycationExperiences.length - 1 || !!extras.length;

        const experienceCategory = experienceCategories?.find(
          (cat) => cat.id === stayExp.category.id
        );

        const expTitle = !!experienceCategory
          ? renderExperienceName(experienceCategory, stayExp)
          : stayExp.category.name;

        return (
          <RenderPaymentDetail
            title={title}
            content={`${expQuantity} x ${expTitle}`}
            value="Included"
            removeDivider={removeDivider}
          />
        );
      })}
      {extras?.map((extra, idx) => {
        const title =
          idx === 0 && !staycationExperiences.length ? 'Extras' : undefined;
        const removeDivider = idx !== extras.length - 1;

        const experienceCategory = experienceCategories?.find(
          (cat) => cat.id === extra.category.id
        );

        const expTitle = !!experienceCategory
          ? renderExperienceName(experienceCategory, extra)
          : extra.category.name;

        return (
          <Fragment key={extra.id}>
            <RenderPaymentDetail
              title={title}
              content={`${extra?.quantity} x ${expTitle}`}
              value={currencyFormatter(extra.quantity * extra.price)}
              removeDivider={removeDivider}
            />
          </Fragment>
        );
      })}
    </div>
  );
};
